import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IAbaTherapy, IAbaTherapyService } from '../../utils/types';

export interface AbaTherapyState {
  treatmentPlanId: number | null;
  therapyHistory: IAbaTherapy | Partial<IAbaTherapy>;
  services: IAbaTherapyService[];
  triggerValidationsOnPageLoad: boolean;
  error: any;
  loading: boolean;
  errorState: any;
}

const name = 'therapyHistory';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<AbaTherapyState>) =>
    createExtraReducers(builder),
});

function createInitialState(): AbaTherapyState {
  return {
    treatmentPlanId: null,
    therapyHistory: {},
    services: [],
    triggerValidationsOnPageLoad: false,
    error: null,
    loading: false,
    errorState: {},
  };
}

function createReducers() {
  return {
    updateState,
    resetState,
    updateEditing,
    updateErrorState,
  };

  function updateState(state: AbaTherapyState, action: any) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function resetState(state: AbaTherapyState) {
    state.treatmentPlanId = null;
    state.therapyHistory = {};
    state.services = [];
    state.triggerValidationsOnPageLoad = false;
    state.errorState = {};
  }
  function updateEditing(state: AbaTherapyState, action: any) {
    const { treatmentPlanId: _treatmentPlanId, actionType, data } = action.payload;
    if (actionType === 'create') {
      state.services = [...state.services, { ...data }];
    }
    if (actionType === 'update') {
      const serviceIndex = state.services.findIndex((service) => !service.id);
      state.services[serviceIndex] = data;
    }
    if (actionType === 'delete') {
      state.services = state.services.filter((service) => !!service.id);
    }
  }
  function updateErrorState(state: AbaTherapyState, action: any) {
    state.errorState = { ...state.errorState, ...action.payload };
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getAbaTherapy: getAbaTherapy(),
    createAbaTherapy: createAbaTherapy(),
    removeAbaTherapy: removeAbaTherapy(),
    updateAbaTherapy: updateAbaTherapy(),
    createService: createService(),
    removeService: removeService(),
    updateService: updateService(),
  };

  function getAbaTherapy() {
    return createAsyncThunk(
      `${name}/getAbaTherapy`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/therapy_history/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function createAbaTherapy() {
    return createAsyncThunk(
      `${name}/createAbaTherapy`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/therapy_history/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }
  function removeAbaTherapy() {
    return createAsyncThunk(
      `${name}/removeAbaTherapy`,
      async ({ getAccessTokenSilently, treatmentPlanId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/therapy_history/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function updateAbaTherapy() {
    return createAsyncThunk(
      `${name}/updateAbaTherapy`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/therapy_history/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }

  function createService() {
    return createAsyncThunk(
      `${name}/createService`,
      async ({ treatmentPlanId, therapyHistoryId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/therapy_history/${therapyHistoryId}/services/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
  function removeService() {
    return createAsyncThunk(
      `${name}/removeService`,
      async ({ treatmentPlanId, therapyHistoryId, serviceId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/therapy_history/${therapyHistoryId}/services/${serviceId}/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function updateService() {
    return createAsyncThunk(
      `${name}/updateService`,
      async ({ treatmentPlanId, therapyHistoryId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/therapy_history/${therapyHistoryId}/services/${data.id}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<AbaTherapyState>) {
  const {
    getAbaTherapy,
    createAbaTherapy,
    removeAbaTherapy,
    updateAbaTherapy,
    createService,
    removeService,
    updateService,
  } = extraActions;
  builder
    // getAbaTherapy()
    .addCase(getAbaTherapy.pending, (state: AbaTherapyState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getAbaTherapy.fulfilled,
      (state: AbaTherapyState, action: PayloadAction<IAbaTherapy>) => {
        const { services } = action.payload;
        const unsavedServices = state.services.filter((service: IAbaTherapyService) => {
          return !service.id;
        });
        state.loading = false;
        state.therapyHistory = action.payload;
        state.services = services ? [...services, ...unsavedServices] : [...unsavedServices];
      },
    )
    .addCase(getAbaTherapy.rejected, (state: AbaTherapyState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createAbaTherapy()
    .addCase(createAbaTherapy.pending, (state: AbaTherapyState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createAbaTherapy.fulfilled,
      (state: AbaTherapyState, action: PayloadAction<IAbaTherapy>) => {
        state.therapyHistory = action.payload;
        state.loading = false;
      },
    )
    .addCase(createAbaTherapy.rejected, (state: AbaTherapyState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // removeAbaTherapy()
    .addCase(removeAbaTherapy.pending, (state: AbaTherapyState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeAbaTherapy.fulfilled, (state: AbaTherapyState) => {
      state.therapyHistory = {};
      state.services = [];
      state.loading = false;
    })
    .addCase(removeAbaTherapy.rejected, (state: AbaTherapyState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateAbaTherapy()
    .addCase(updateAbaTherapy.pending, (state: AbaTherapyState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateAbaTherapy.fulfilled,
      (state: AbaTherapyState, action: PayloadAction<IAbaTherapy>) => {
        const { services } = action.payload;
        const unsavedServices = state.services.filter((service: IAbaTherapyService) => {
          return !service.id;
        });
        state.loading = false;
        state.therapyHistory = action.payload;
        state.services = services ? [...services, ...unsavedServices] : [...unsavedServices];
      },
    )
    .addCase(updateAbaTherapy.rejected, (state: AbaTherapyState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createService()
    .addCase(createService.pending, (state: AbaTherapyState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      createService.fulfilled,
      (state: AbaTherapyState, action: PayloadAction<IAbaTherapyService>) => {
        // remove the service being edited in local by the service coming from server into state fo services.
        const savedServices = (state.services = state.services.filter(
          (service: IAbaTherapyService) => !!service.id,
        ));
        state.services = [...savedServices, action.payload];
        state.loading = false;
        toast.success('Service created successfully');
      },
    )
    .addCase(createService.rejected, (state: AbaTherapyState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // removeService()
    .addCase(
      removeService.pending,
      (state: AbaTherapyState, _action: PayloadAction<IAbaTherapyService>) => {
        state.loading = true;
        state.error = null;
      },
    )
    .addCase(
      removeService.fulfilled,
      (
        state: AbaTherapyState,
        action: PayloadAction<IAbaTherapyService, string, { arg: { serviceId: number } }>,
      ) => {
        state.services = state.services.filter(
          (service) => service.id !== action.meta.arg.serviceId,
        );
        delete state.errorState[`services-${action.meta.arg.serviceId}`];
        delete state.errorState[`services-new`];
        state.loading = false;
        toast.success('Service deleted successfully');
      },
    )
    .addCase(removeService.rejected, (state: AbaTherapyState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateServices()
    .addCase(updateService.pending, (state: AbaTherapyState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateService.fulfilled,
      (
        state: AbaTherapyState,
        action: PayloadAction<IAbaTherapyService, string, { arg: { serviceId: number } }>,
      ) => {
        const updatedService = action.payload;
        const savedServices: IAbaTherapyService[] = [],
          unsavedServices: IAbaTherapyService[] = [];

        state.services.forEach((service: IAbaTherapyService) => {
          if (!service.id) {
            unsavedServices.push(service);
          }
          if (service.id) {
            if (service.id === action.meta.arg.serviceId) {
              savedServices.push(updatedService);
            } else {
              savedServices.push(service);
            }
          }
        });
        state.services = [...savedServices, ...unsavedServices];
        state.loading = false;
      },
    )
    .addCase(updateService.rejected, (state: AbaTherapyState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const abaTherapyServicesActions = { ...slice.actions, ...extraActions };
export const abaTherapyServicesReducer = slice.reducer;
